<template>
  <div>
    <SidebarContainer>
      <template #default>
        <div class="setting-content-item">
          <div class="content-preview">
            <div
              class="preview-body scrollbar"
              v-if="
                item.content_type !== 'folder' &&
                ['video', 'audio', 'image', 'pdf'].includes(getSimpleType(item.item_type))
              "
            >
              <img :src="item.img" alt="preview" v-if="getSimpleType(item.item_type) === 'image'" />
              <video
                v-else-if="getSimpleType(item.item_type) === 'video'"
                :key="item.item_id"
                muted
                preload="metadata"
                controls
                style="object-fit: fill"
                class="w-100 h-100"
                id="right-sidebar-preview-video"
              >
                <source :src="`${getEncodedURL(baseUrl, item.item_url)}`" :type="item.item_type" />
              </video>
              <AudioView
                v-else-if="getSimpleType(item.item_type) === 'audio'"
                :key="item.item_id"
                :src="`${getEncodedURL(baseUrl, item.item_url)}`"
                :type="item.item_type"
              />
              <PdfView
                v-else-if="item.item_type.includes('pdf')"
                :pdfImages="item.pdf_content.thumbnail_url"
              />
            </div>

            <div class="preview-body scrollbar" v-else-if="item?.item_type?.startsWith('app')">
              <RssViewer
                v-if="item.item_type === 'app/rss'"
                :key="item.item_id"
                class="w-100 h-100"
                :setting="rssAppSetting"
                isPreview
              />
              <TableAppWebView
                v-else-if="item.item_type === 'app/table'"
                :key="item.item_id"
                class="w-100 h-100"
                :settings="item"
              />
              <TrafiklabViewer
                v-else-if="item.item_type === 'app/trafiklab'"
                :key="item.item_id"
                :viewerStyles="item.viewerStyles"
                :timetable="item.timetable"
                :name="item.name"
                :app-id="item.app_id"
              />
              <WeatherView
                v-else-if="item.item_type === 'app/weather'"
                :key="item.item_id"
                :app="item"
                isPreview
              />
            </div>
            <div class="preview-footer">
              <div class="type">
                <i class="material-icons-outlined align-middle"> crop_original </i>
                <span v-if="item.item_type === CONTENT_TYPE_TABLE_APP" class="name">{{
                  CONTENT_TYPE_TENANT
                }}</span>
                <span v-else class="name">{{
                  item.item_type ? getSimpleType(item.item_type) : getSimpleType(item.type)
                }}</span>
              </div>
              <!-- <div class="expand">
                <i class="material-icons-outlined align-middle button"> fullscreen </i>
              </div> -->
            </div>
          </div>

          <div class="content-body">
            <Input label="File Name" @input="setContentName" :value="contentName" id="name" />
            <Select
              label="Folder"
              id="folder"
              :value="contentFolder"
              @input="setContentFolder"
              :options="folders"
            />
            <SidebarDropdown
              title="Info"
              :default-open="true"
              :items="[
                {
                  name: 'Resolution',
                  value:
                    item?.item_resolution_width && item?.item_resolution_height
                      ? `${item.item_resolution_width}x${item.item_resolution_height}`
                      : false,
                },
                {
                  name: 'Duration',
                  value: item?.last_pushed_duration
                    ? formatSeconds(item.last_pushed_duration)
                    : false,
                },
                {
                  name: 'Size',
                  value: item.size ? humanFileSize(item.size) : false,
                },
                {
                  name: 'Last Modified',
                  value: item.last_modified ? formattedDate(item.last_modified) : false,
                },
                {
                  name: 'Created At',
                  value: item?.created ? formattedDate(item.created) : false,
                },
                {
                  name: 'Path',
                  value: item.path ? item.path : false,
                },
              ]"
            />
          </div>
        </div>
      </template>

      <template #buttons>
        <BaseButton :loading="isSaving" icon="save" @click="saveContentChanges">
          Save Changes
        </BaseButton>
        <Button theme="secondary" icon="delete_outline" @click.native="handleDelete">
          Delete File
        </Button>
      </template>
    </SidebarContainer>

    <ContentDeleteModal
      :content="item"
      v-if="showDeleteModal"
      @onDelete="$emit('onDelete')"
      @closeModal="showDeleteModal = false"
    />
  </div>
</template>

<script>
  import Input from '@/components/common/Input.vue';
  import Select from '@/components/common/Select.vue';
  import SidebarContainer from '@/components/common/SidebarContainer.vue';
  import SidebarDropdown from '@/components/common/SidebarDropdown.vue';
  import Button from '@/components/common/Button.vue';
  import TrafiklabViewer from '@/components/common/timetable/TrafiklabViewer.vue';
  import PdfView from '@/components/content/PdfView.vue';
  import RssViewer from '@/components/rssViewer/RssViewer.vue';
  import TableAppWebView from '@/components/tableApp/TableAppWebView.vue';
  import WeatherView from '@/components/apps/weather/WeatherView.vue';
  import ContentDeleteModal from '@/components/content/ContentDeleteModal.vue';
  import { formatSeconds, humanFileSize } from '@/helpers/filters';
  import { simpleTypeMixin } from '@/helpers';
  import { formattedDate, getRSSAppSettings } from '@/helpers/utils';
  import { CONTENT_TYPE_TABLE_APP, CONTENT_TYPE_TENANT } from '@/constant/const';
  import AudioView from '@/components/content/AudioView.vue';
  import { apiUpdateContent } from '@/api/content';

  export default {
    props: {
      item: {
        type: Object,
        default() {
          return {};
        },
      },
    },

    mixins: [simpleTypeMixin],

    components: {
      AudioView,
      ContentDeleteModal,
      SidebarContainer,
      Input,
      Select,
      SidebarDropdown,
      Button,
      TrafiklabViewer,
      PdfView,
      RssViewer,
      TableAppWebView,
      WeatherView,
    },

    data() {
      return {
        showDeleteModal: false,
        showDirectoryModal: false,
        CONTENT_TYPE_TENANT,
        CONTENT_TYPE_TABLE_APP,
        isSaving: false,
        contentName: null,
        contentFolder: null,
      };
    },

    computed: {
      rssAppSetting() {
        return getRSSAppSettings(this.item);
      },
      folders() {
        return this.$store.getters.getAllContentFolders.map((folder) => {
          return {
            label: folder.name,
            value: folder.id,
          };
        });
      },
    },

    watch: {
      item: {
        immediate: true,
        deep: true,
        handler(newItem) {
          this.contentFolder = newItem?.item_folder || null;
          this.contentName = newItem?.name || null;
        },
      },
    },

    methods: {
      formatSeconds,
      formattedDate,
      humanFileSize,
      handleDelete() {
        this.showDeleteModal = true;
      },
      async saveContentChanges() {
        this.isSaving = true;
        try {
          await apiUpdateContent(this.item.item_id, this.contentFolder, this.contentName);
          this.$toasted.global.general_success({ message: 'Content successfully updated' });
          this.$emit('onUpdate');
        } catch (err) {
          this.$toasted.global.general_error({ message: 'Failed to update the content' });
        } finally {
          this.isSaving = false;
        }
      },
      setContentName(name) {
        this.contentName = name;
      },
      setContentFolder(folder) {
        this.contentFolder = folder;
      },
    },
  };
</script>

<style lang="scss">
  .setting-content-item {
    .content-preview {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 20px;

      .preview-body {
        display: flex;
        flex-direction: column;
        height: 300px;
        min-height: 190px;
        overflow: auto;
      }

      img {
        width: 100%;
      }

      .preview-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .type {
          display: flex;
          gap: 4px;

          i {
            font-size: 20px;
            line-height: 21px;
            color: #6a6b6a;
          }

          .name {
            font-weight: 500;
            font-size: 14px;
            line-height: 21px;
            color: #333332;
            text-transform: capitalize;
          }
        }

        .expand {
          i {
            font-size: 20px;
            line-height: 21px;
            color: #6a6b6a;
          }
        }
      }
    }

    .content-body {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
  }
</style>
